import { useEffect, useState } from "react";
import { api, apiErr, API } from "@/api";
import Overlay from "@/components/Overlay";
import PhotoIntake from "@/components/PhotoIntake";
import CatalogueModal from "@/components/CatalogueModal";
import { imagesOf } from "@/components/ProductImages";
import { useTaxonomy } from "@/hooks/useTaxonomy";
import LibraryPicker from "./LibraryPicker";
import { toast } from "sonner";
import { X, Plus, Camera, ArrowUp, ArrowDown, Trash2, Star, Eye, Save, Send } from "lucide-react";

const inputCls = "w-full bg-black/30 gold-border rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:outline-none";

function Row({ pr, i, n, isCover, move, remove, setCover }) {
  const btn = "h-7 w-7 rounded-lg glass flex items-center justify-center text-slate-400 disabled:opacity-30";
  return (
    <div className="flex items-center gap-2 glass rounded-xl p-2" data-testid={`builder-row-${pr.id}`}>
      <span className="text-[11px] text-slate-500 w-4 text-center">{i + 1}</span>
      <img src={`${API}/files/${imagesOf(pr)[0]}`} alt="" className="h-11 w-11 rounded-lg object-cover" />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-slate-100 truncate">{pr.name}</div>
        <div className="text-[10px] text-amber-300">{pr.category}{isCover ? " · Cover" : ""}</div>
      </div>
      <button onClick={setCover} className={`${btn} ${isCover ? "text-amber-500" : ""}`} title="Use as cover" data-testid={`builder-cover-${pr.id}`}><Star size={13} fill={isCover ? "currentColor" : "none"} /></button>
      <button disabled={i === 0} onClick={() => move(-1)} className={btn} data-testid={`builder-up-${pr.id}`}><ArrowUp size={13} /></button>
      <button disabled={i === n - 1} onClick={() => move(1)} className={btn} data-testid={`builder-down-${pr.id}`}><ArrowDown size={13} /></button>
      <button onClick={remove} className={`${btn} text-red-400`} data-testid={`builder-remove-${pr.id}`}><Trash2 size={13} /></button>
    </div>
  );
}

export default function CatalogueBuilder({ catalogueId = null, appendIds = [], credits, setCredits, costs, onClose, onSaved }) {
  const tax = useTaxonomy();
  const [cat, setCat] = useState(null);
  const [draft, setDraft] = useState({ title: "", intro: "", product_ids: [], cover_image: "" });
  const [lib, setLib] = useState(null);
  const [picker, setPicker] = useState(false);
  const [intake, setIntake] = useState(false);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState("");
  const [ready, setReady] = useState(!catalogueId);

  useEffect(() => {
    const merge = (d) => ({ ...d, product_ids: [...d.product_ids, ...appendIds.filter((x) => !d.product_ids.includes(x))] });
    api.get("/exhibitor/products").then(({ data }) => setLib(data));
    if (catalogueId) {
      api.get(`/exhibitor/catalogues/${catalogueId}/detail`).then(({ data }) => { setCat(data); setDraft(merge({ title: "", intro: "", cover_image: "", ...data.draft })); setReady(true); });
    } else {
      setDraft((d) => merge(d));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogueId]);

  const byId = Object.fromEntries((lib || []).map((p) => [p.id, p]));
  const chosen = draft.product_ids.map((id) => byId[id]).filter(Boolean);
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const add = (ids) => set({ product_ids: [...draft.product_ids, ...ids.filter((x) => !draft.product_ids.includes(x))] });
  const move = (i, d) => { const ids = [...draft.product_ids]; [ids[i], ids[i + d]] = [ids[i + d], ids[i]]; set({ product_ids: ids }); };
  const coverImg = draft.cover_image || (chosen[0] && imagesOf(chosen[0])[0]) || "";

  const saveDraft = async () => {
    const body = { ...draft, product_ids: chosen.map((p) => p.id) };
    if (cat) { const { data } = await api.put(`/exhibitor/catalogues/${cat.id}/draft`, body); setCat(data); return data.id; }
    const { data } = await api.post("/exhibitor/catalogues/draft", body);
    setCat(data);
    return data.id;
  };
  const run = async (kind) => {
    if (!draft.title.trim()) { toast.error("Give the catalogue a title"); return; }
    setBusy(kind);
    try {
      const id = await saveDraft();
      if (kind === "publish") {
        const { data } = await api.post(`/exhibitor/catalogues/${id}/publish`);
        setCat(data);
        toast.success(cat?.status === "published" ? "Updates published · share links unchanged" : "Catalogue published");
        onSaved?.();
      } else { toast.success("Draft saved"); }
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(""); }
  };

  const published = cat?.status === "published" || (cat && !cat.status);
  const previewCat = { id: "preview", type: "ai", title: draft.title, ai_content: { title: draft.title, intro: draft.intro, cover_image: coverImg, products: chosen.map((p) => ({ ...p, product_id: p.id })) } };

  return (
    <Overlay onClose={() => !busy && onClose()} className="max-w-2xl" testid="assemble-modal">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-2xl gold-text">{cat ? "Edit Catalogue" : "New Catalogue"}</h3>
          <p className="text-[11px] text-slate-500" data-testid="builder-status">{!cat ? "Not saved yet" : published ? (cat.has_unpublished_changes ? "Published · unpublished changes" : "Published") : "Draft"}</p>
        </div>
        <button onClick={() => !busy && onClose()} className="text-slate-400" data-testid="assemble-close"><X size={20} /></button>
      </div>
      <fieldset disabled={!ready} className={ready ? "" : "opacity-60"} data-testid="builder-fields">
      <input value={draft.title} onChange={(e) => set({ title: e.target.value })} placeholder="Catalogue title *" className={`${inputCls} mt-4`} data-testid="assemble-title" />
      <textarea value={draft.intro} onChange={(e) => set({ intro: e.target.value })} placeholder="Intro (optional)" rows={2} className={`${inputCls} mt-2`} data-testid="assemble-intro" />
      <div className="flex flex-wrap items-center justify-between gap-2 mt-4">
        <p className="text-xs text-slate-400" data-testid="assemble-picked-count">{chosen.length} products</p>
        <div className="flex gap-2">
          <button onClick={() => setPicker(true)} className="text-xs px-3 py-1.5 rounded-full btn-gold flex items-center gap-1" data-testid="builder-add-products"><Plus size={13} /> Add Products</button>
          <button onClick={() => setIntake(true)} className="text-xs px-3 py-1.5 rounded-full glass border border-amber-500/20 text-slate-200 flex items-center gap-1" data-testid="builder-upload-photos"><Camera size={13} className="text-amber-400" /> Upload new photos</button>
        </div>
      </div>
      {!lib || !ready ? <p className="text-sm text-slate-400 mt-4" data-testid="builder-loading">Loading…</p> : chosen.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-amber-500/30 rounded-2xl mt-3" data-testid="builder-empty">
          <p className="text-slate-400 text-sm">Add products from My Products, or upload new photos.</p>
        </div>
      ) : (
        <div className="space-y-1.5 mt-3">
          {chosen.map((pr, i) => (
            <Row key={pr.id} pr={pr} i={i} n={chosen.length} isCover={coverImg === imagesOf(pr)[0]} move={(d) => move(i, d)}
              remove={() => set({ product_ids: draft.product_ids.filter((x) => x !== pr.id) })} setCover={() => set({ cover_image: imagesOf(pr)[0] })} />
          ))}
        </div>
      )}
      </fieldset>
      <div className="grid grid-cols-3 gap-2 mt-5">
        <button disabled={!chosen.length} onClick={() => setPreview(true)} className="glass py-3 rounded-xl text-sm text-slate-200 border border-amber-500/20 flex items-center justify-center gap-1.5 disabled:opacity-50" data-testid="builder-preview"><Eye size={15} /> Preview</button>
        <button disabled={!!busy || !ready} onClick={() => run("draft")} className="glass py-3 rounded-xl text-sm text-slate-200 border border-amber-500/20 flex items-center justify-center gap-1.5 disabled:opacity-50" data-testid="builder-save-draft"><Save size={15} /> {busy === "draft" ? "Saving…" : "Save draft"}</button>
        <button disabled={!!busy || !ready || !chosen.length} onClick={() => run("publish")} className="btn-gold py-3 rounded-xl text-sm flex items-center justify-center gap-1.5 disabled:opacity-50" data-testid="assemble-submit"><Send size={15} /> {busy === "publish" ? "Publishing…" : published ? "Publish Updates" : "Publish"}</button>
      </div>
      {picker && lib && <LibraryPicker lib={lib} tax={tax} already={draft.product_ids} onClose={() => setPicker(false)} onAdd={(ids) => { add(ids); setPicker(false); }} />}
      {intake && (
        <PhotoIntake credits={credits} setCredits={setCredits} costs={costs} onClose={() => setIntake(false)}
          onApproved={(p) => { setLib((l) => [p, ...(l || []).filter((x) => x.id !== p.id)]); setDraft((d) => ({ ...d, product_ids: d.product_ids.includes(p.id) ? d.product_ids : [...d.product_ids, p.id] })); }} />
      )}
      {preview && <CatalogueModal catalogue={previewCat} onClose={() => setPreview(false)} />}
    </Overlay>
  );
}
